<template>
  <div>
    <h3>Introduction de la page d'accueil</h3>
    
    <!-- Text Editor -->
    <b-form-group label="Texte d'introduction :">
      <Editor 
        :api-key="tinyMceKey" 
        :init="tinySettings" 
        v-model="introductionText" 
      />
    </b-form-group>
    
    <b-button @click="saveIntroduction" variant="primary" class="mb-3">
      Sauvegarder le texte
    </b-button>

    <hr class="my-4">

    <!-- Image Management -->
    <h4>Images d'introduction</h4>
    
    <!-- Current Images -->
    <div class="mb-3">
      <h5>Images actuelles (glissez-déposez pour réorganiser) :</h5>
      <div class="row" ref="imageContainer">
        <div 
          v-for="(image, index) in currentImages" 
          :key="image" 
          class="col-md-3 mb-3"
          draggable="true"
          @dragstart="dragStart($event, index)"
          @dragover.prevent
          @drop="drop($event, index)"
          @dragenter.prevent
        >
          <div class="card" :class="{ 'dragging': draggedIndex === index }">
            <div class="card-header bg-light">
              <small class="text-muted">Ordre: {{ index + 1 }}</small>
            </div>
            <img :src="backendUrl + '/introduction/' + image" class="card-img-top" alt="Introduction image">
            <div class="card-body">
              <p class="card-text">{{ image }}</p>
              <b-button @click="deleteImage(image)" variant="danger" size="sm">
                Supprimer
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <b-button @click="saveOrder" variant="info" class="mt-2" :disabled="!orderChanged">
        Sauvegarder l'ordre
      </b-button>
    </div>

    <!-- Add New Image -->
    <div class="mb-3">
      <h5>Ajouter une nouvelle image :</h5>
      <b-form-file
        v-model="newImage"
        accept="image/*"
        placeholder="Choisissez une image..."
        class="mb-2"
      ></b-form-file>
      <b-button @click="addImage" variant="success" :disabled="!newImage">
        Ajouter l'image
      </b-button>
    </div>

    <!-- Modals -->
    <b-modal v-model="okModal" title="Sauvegardé" ok-only>
      <div>
        Introduction sauvegardée
      </div>
    </b-modal>
    
    <b-modal v-model="okImageModal" title="Image ajoutée" ok-only>
      <div>
        Image ajoutée avec succès
      </div>
    </b-modal>
    
    <b-modal v-model="okDeleteImageModal" title="Image supprimée" ok-only>
      <div>
        Image supprimée avec succès
      </div>
    </b-modal>
    
    <b-modal v-model="okOrderModal" title="Ordre sauvegardé" ok-only>
      <div>
        Ordre des images sauvegardé
      </div>
    </b-modal>
    
    <b-modal v-model="errorModal" title="Oups !!!" ok-only>
      <div>
        <p>Une erreur est survenue :</p>
        <p v-if="errorMessage" class="text-danger">{{ errorMessage }}</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import Editor from '@tinymce/tinymce-vue'

export default {
  name: 'AdminIntroduction',
  components: {
    Editor
  },
  data() {
    return {
      introductionText: '',
      currentImages: [],
      newImage: null,
      okModal: false,
      okImageModal: false,
      okDeleteImageModal: false,
      okOrderModal: false,
      errorModal: false,
      errorMessage: '',
      backendUrl: Vue.$backend.getBackendUrl(),
      tinyMceKey: Vue.$tinykey,
      draggedIndex: null,
      orderChanged: false,
      originalOrder: [],
      tinySettings: {
        height: 300,
        menubar: false,
        plugins: [
          'advlist autolink lists link image charmap print preview anchor',
          'searchreplace visualblocks code fullscreen',
          'insertdatetime media table paste code help wordcount'
        ],
        toolbar:
          'undo redo | formatselect | bold italic backcolor | \
          alignleft aligncenter alignright alignjustify | \
          bullist numlist outdent indent | removeformat | help'
      }
    }
  },
  mounted() {
    this.loadIntroduction()
    this.loadImages()
  },
  methods: {
    loadIntroduction() {
      Vue.$backend.getHomepageIntroduction().then((data) => {
        this.introductionText = data.text
      }).catch((error) => {
        console.error('Error loading introduction:', error)
        this.errorMessage = this.extractErrorMessage(error)
        this.errorModal = true
      })
    },
    
    loadImages() {
      Vue.$backend.getHomepageIntroductionImages().then((data) => {
        this.currentImages = data.images
        this.originalOrder = [...data.images]
        this.orderChanged = false
      }).catch((error) => {
        console.error('Error loading images:', error)
        this.errorMessage = this.extractErrorMessage(error)
        this.errorModal = true
      })
    },
    
    saveIntroduction() {
      Vue.$backend.updateHomepageIntroduction(this.introductionText).then(() => {
        this.okModal = true
      }).catch((error) => {
        console.error('Error saving introduction:', error)
        this.errorMessage = this.extractErrorMessage(error)
        this.errorModal = true
      })
    },
    
    addImage() {
      if (!this.newImage) return
      
      Vue.$backend.addHomepageIntroductionImage(this.newImage).then((response) => {
        this.okImageModal = true
        this.newImage = null
        this.loadImages()
      }).catch((error) => {
        console.error('Error adding image:', error)
        this.errorMessage = this.extractErrorMessage(error)
        this.errorModal = true
      })
    },
    
    deleteImage(filename) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
        Vue.$backend.deleteHomepageIntroductionImage(filename).then(() => {
          this.okDeleteImageModal = true
          this.loadImages()
        }).catch((error) => {
          console.error('Error deleting image:', error)
          this.errorMessage = this.extractErrorMessage(error)
          this.errorModal = true
        })
      }
    },
      
      dragStart(event, index) {
        this.draggedIndex = index
        event.dataTransfer.effectAllowed = 'move'
      },
      
      drop(event, dropIndex) {
        if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
          this.draggedIndex = null
          return
        }
        
        // Reorder the array
        const images = [...this.currentImages]
        const draggedImage = images[this.draggedIndex]
        images.splice(this.draggedIndex, 1)
        images.splice(dropIndex, 0, draggedImage)
        
        this.currentImages = images
        this.orderChanged = true
        this.draggedIndex = null
      },
      
      saveOrder() {
        Vue.$backend.reorderHomepageIntroductionImages(this.currentImages).then(() => {
          this.originalOrder = [...this.currentImages]
          this.orderChanged = false
          this.okOrderModal = true
          this.loadImages() // Reload to get updated filenames
        }).catch((error) => {
          console.error('Error saving order:', error)
          this.errorMessage = this.extractErrorMessage(error)
          this.errorModal = true
        })
      },
      
      extractErrorMessage(error) {
      if (error.body && error.body.error) {
        return error.body.error
      } else if (error.statusText) {
        return error.statusText
      } else if (error.message) {
        return error.message
      } else {
        return 'Erreur inconnue'
      }
    }
  }
}
</script>

<style scoped>
.card-img-top {
  height: 150px;
  object-fit: cover;
}

.dragging {
  opacity: 0.5;
  transform: scale(0.95);
}

.card {
  cursor: grab;
  transition: all 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.card:active {
  cursor: grabbing;
}
</style>
